// Scroll story (sections/kb-scroll-story.liquid). The section only loads this file when motion is on.
// 2 Nov (council, "Apple-level finish"): replaces kb-scroll-story.js. The frames come from assets/kb-story-frames.js
// (one frame pack per clip, decoded a frame at a time); the picture follows the scroll through a short, time-based
// ease, so wheel notches and fast flicks glide instead of jumping, and nothing is measured or rewritten per frame.
(() => {
const init = (story) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.kbStoryFrames) { story.classList.add('kb-story--static'); return; }
  const data = JSON.parse(story.querySelector('.kb-story__data').textContent);
  // the delivery beat (d1–d3) is off since the 2 Nov meeting, so its clips aren't loaded at all
  const ship = story.dataset.ship !== '0';
  const ORDER = ship ? ['b1', 'b2', 'b3', 'c3', 'b4', 'd1', 'd2', 'd3'] : ['b1', 'b2', 'b3', 'c3', 'b4'];
  const iconNames = data.iconNames;
  const pin = story.querySelector('.kb-story__pin');
  const intro = story.querySelector('.kb-story__intro');
  const items = [...story.querySelectorAll('.kb-story__pt')];
  const rowSlots = [...story.querySelectorAll('.kb-story__row i')];
  const colSlots = [...story.querySelectorAll('.kb-story__col i')];
  const startImg = story.querySelector('.kb-story__start');
  const canvas = story.querySelector('.kb-story__seq');
  const roundel = story.querySelector('.kb-story__roundel');
  const zoomEl = story.querySelector('.kb-story__zoom');
  const win = story.querySelector('.kb-story__win');
  const winIcons = win ? [...win.querySelectorAll('span')] : [];
  const iconName = story.querySelector('.kb-story__icon-name');
  const shipRows = [...story.querySelectorAll('.kb-story__ship div')];
  const cap = story.querySelector('.kb-story__cap');
  const bar = story.querySelector('.kb-story__rail i');
  const header = document.querySelector('.site-header');
  const root = document.documentElement;
  const phone = window.matchMedia('(max-width: 760px)');
  const capStart = cap.textContent;
  const caps = items.map((it) => it.dataset.cap);
  // 2 Nov meeting: without the delivery beat the scroll stops on the zipped pair once b4 has played back
  // (the section is shorter to match, so the pace is the same). b4 (v3) now unzips, slides the yellow bag away and
  // moves in to the single bag's K, so it gets 0.11 of the scroll; the icons and everything after moved 0.045 later.
  const STOP = 0.7695, SPAN = 0.79;

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const seg = (p, a, b) => clamp((p - a) / (b - a));
  const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const lerp = (a, b, t) => a + (b - a) * t;

  // style, text and class writes only when the value changes, so a still frame costs no style or layout work
  const memo = new WeakMap();
  const put = (el, prop, v) => {
    let m = memo.get(el);
    if (!m) memo.set(el, (m = {}));
    if (m[prop] === v) return;
    m[prop] = v;
    if (prop === 'text') el.textContent = v;
    else if (prop === 'hidden') el.hidden = v;
    else el.style[prop] = v;
  };
  const flag = (el, c, on) => { if (el.classList.contains(c) !== on) el.classList.toggle(c, on); };

  let dirty = true;
  const frames = window.kbStoryFrames({ sheets: data.sheets, order: ORDER, canvas, pick: (p) => pick(p), end: ship ? 1 : STOP, phone, changed: () => { dirty = true; wake(); } });
  // Speed (3 Nov): the frame packs are big (0.9 MB phone, 2.3 MB desktop) and only move on scroll, so wait for the
  // visitor's first scroll, tap or key before fetching them; until then the story's start photo shows
  let near = false, engaged = false;
  const go = () => { if (near && engaged && !off) frames.load(0); };
  const engage = () => {
    engaged = true; go();
    ['scroll', 'pointerdown', 'touchstart', 'keydown', 'wheel'].forEach((t) => window.removeEventListener(t, engage));
  };
  ['scroll', 'pointerdown', 'touchstart', 'keydown', 'wheel'].forEach((t) => window.addEventListener(t, engage, { passive: true }));
  new IntersectionObserver((entries, obs) => {
    if (entries.some((e) => e.isIntersecting)) { near = true; go(); obs.disconnect(); }
  }, { rootMargin: '150% 0px' }).observe(story);

  // which frames show at scroll position p: [clip, point in clip 0..1, opacity]
  const pick = (p) => {
    if (p < 0.09) return [['b1', 0, 1]];
    if (p < 0.16) return [['b1', seg(p, 0.09, 0.16), 1]];
    if (p < 0.22) return [['b2', seg(p, 0.16, 0.22), 1]];
    // shot changes are hard cuts between matching frames (b3's last = c3's last, c3's first = b4's first): no blending
    if (p < 0.28) return [['b3', seg(p, 0.22, 0.28), 1]];
    if (p < 0.2875) return [['b3', 1, 1]];
    if (p < 0.295) return [['c3', 1, 1]];
    if (p < 0.4) return [['c3', 1 - seg(p, 0.295, 0.4), 1]];
    if (p < 0.45) return [['c3', 0, 1]];
    if (p < 0.455) return [['b4', 0, 1]];
    if (p < 0.565) return [['b4', seg(p, 0.455, 0.565), 1]];
    if (p < 0.69) return [['b4', 1, 1]];
    if (p < 0.77) return [['b4', 1 - seg(p, 0.69, 0.77), 1]];
    if (p < 0.83) return [['d1', seg(p, 0.77, 0.83), 1]];
    if (p < 0.89) return [['d2', seg(p, 0.83, 0.89), 1]];
    if (p < 0.96) return [['d3', seg(p, 0.89, 0.96), 1]];
    return [['d3', 1, 1]];
  };
  // ---- geometry, measured once and again on resize (never per frame) --------------------------------------------
  let geo = null, rects = null;
  // the pin sits under the sticky announcement bar and header (the header keeps --kb-header-h up to date)
  const headerH = () => parseFloat(getComputedStyle(root).getPropertyValue('--kb-header-h'));
  const measure = () => {
    const h = headerH();
    const stuck = h >= 0 ? h : header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
    story.style.setProperty('--kb-top', `${stuck}px`);
    const top = story.getBoundingClientRect().top + scrollY;
    geo = { top, stuck, total: Math.max(1, story.offsetHeight - pin.offsetHeight) };
    const base = pin.getBoundingClientRect();
    const r = (el) => { const b = el.getBoundingClientRect(); return { x: b.left - base.left, y: b.top - base.top, w: b.width, h: b.height }; };
    const col = colSlots.map(r);
    rects = { row: phone.matches ? col : rowSlots.map(r), col };
    frames.fit();
    dirty = true;
  };
  const rawAt = (y) => clamp((y + geo.stuck - geo.top) / geo.total);
  const yFor = (raw) => Math.round(geo.top - geo.stuck + raw * geo.total);

  // ---- render at the smoothed position ---------------------------------------------------------------------------
  const render = (raw, dir) => {
    const p = ship ? raw : Math.min(STOP, raw * SPAN);
    flag(story, 'is-buying', p > 0.075);

    // the copy fades and the points drop into a list, one after another
    const fade = phone.matches ? 0 : seg(p, 0, 0.035);
    put(intro, 'opacity', (1 - fade).toFixed(3));
    put(intro, 'transform', `translateY(${(-24 * fade).toFixed(1)}px)`);
    items.forEach((it, i) => {
      const t = ease(seg(p, 0.01 + i * 0.009, 0.047 + i * 0.009));
      const a = rects.row[i], b = rects.col[i];
      if (!a || !b) return;
      put(it, 'width', `${lerp(a.w, b.w, t).toFixed(1)}px`);
      put(it, 'transform', `translate(${lerp(a.x, b.x, t).toFixed(1)}px, ${lerp(a.y, b.y, t).toFixed(1)}px)`);
    });
    // the puddle stop (0.22) still belongs to the waterproof point; the zip point lights as the yellow bag comes in
    const beat = p < 0.06 ? 0 : p < 0.225 ? 1 : p < 0.45 ? 2 : p < 0.69 || !ship ? 3 : 4;
    flag(story, 'is-running', beat > 0);
    items.forEach((it, i) => flag(it, 'is-active', beat === i + 1));

    // the opening photo (the pair) dips to white, then the first frame comes up: no double exposure between them
    const layers = pick(p);
    frames.keep(p, dir);
    const shown = frames.draw(layers) || frames.shown();
    // until the first frames are in, the photo stays put rather than fading to an empty stage
    const out = shown ? seg(p, 0.05, 0.068) : 0, up = shown ? seg(p, 0.068, 0.088) : 0;
    if (startImg) put(startImg, 'opacity', (1 - out).toFixed(3));
    put(canvas, 'opacity', up.toFixed(3));

    // the pair offer pops in once (a 280 ms spring in the CSS) as the bags zip, and stays until the turn to the K
    if (roundel) {
      const on = p >= 0.3 && p < 0.45;
      flag(roundel, 'is-shown', on);
      flag(roundel, 'is-on', on);
    }
    // club icons in the K window
    if (win) {
      const winOn = seg(p, 0.565, 0.57) * (1 - seg(p, 0.675, 0.685));
      put(win, 'opacity', winOn.toFixed(3));
      put(iconName, 'opacity', winOn.toFixed(3));
      // the zoom-in is done by 0.574, so the "icons" stop (0.575) rests on the first card, fully zoomed
      const zin = ease(seg(p, 0.565, 0.574)) * (1 - ease(seg(p, 0.66, 0.685)));
      put(zoomEl, 'transform', `scale(${(1 + 0.12 * zin).toFixed(3)})`);
      // the first card slides down over the K, then each card pushes the last one out, so the window is never empty
      const u = seg(p, 0.567, 0.675) * winIcons.length - 0.2;
      winIcons.forEach((el, i) => {
        const l = u - i;
        const y = l < -0.2 ? -100 : l < 0 ? -100 * (1 - ease((l + 0.2) / 0.2)) : l < 0.8 ? 0 : l < 1 ? 100 * ease((l - 0.8) / 0.2) : 100;
        put(el, 'transform', `translateY(${y.toFixed(1)}%)`);
      });
      const k = clamp(Math.floor(u + 0.1), 0, winIcons.length - 1);
      put(iconName, 'text', iconNames[k] ? `${iconNames[k]} · ${k + 1} of ${winIcons.length}` : '');
    }
    // delivery: next day appears over the van, then free 3–5 day delivery at the door
    shipRows.forEach((el, i) => {
      const t = ease(seg(p, i ? 0.905 : 0.84, i ? 0.925 : 0.86));
      put(el, 'opacity', t.toFixed(3));
      put(el, 'transform', `translateY(${(-14 * (1 - t)).toFixed(1)}px)`);
    });
    flag(cap, 'is-offer', beat === 2);
    const capText = beat ? caps[beat - 1] || '' : capStart;
    put(cap, 'text', capText);
    // on a phone the caption wraps onto the club name in the K, so it steps aside while the club names show
    put(cap, 'hidden', !capText || (phone.matches && (beat === 4 || (!!win && p >= 0.565 && p < 0.685))));
    put(bar, 'transform', `scaleX(${raw.toFixed(4)})`);
  };

  // ---- drawing: the picture follows the story position -----------------------------------------------------------
  // In stepped mode (below) the position comes from a timed action and is drawn as it is. When the story is scrubbed
  // freely (coming back up into it from below, after Skip) the picture eases after the scroll instead: 100 ms behind a
  // wheel or trackpad, 70 ms behind a finger. Either way a still page costs nothing.
  let target = 0, cur = 0, raf = 0, lastT = 0, dir = 1, touchInput = false, off = false, free = !!ship;
  const tick = (now) => {
    raf = 0;
    if (off) return;
    if (!geo) measure();
    const dt = lastT ? Math.min(64, now - lastT) : 16;
    lastT = now;
    const tau = touchInput ? 70 : 100;
    const gap = target - cur;
    cur = !free || Math.abs(gap) > 0.2 ? target : cur + gap * (1 - Math.exp(-dt / tau));
    if (Math.abs(target - cur) < 0.00004) cur = target;
    const exact = cur === target;
    render(cur, dir);
    dirty = false;
    // keep going while the picture is catching up, or while a frame it wants is still decoding
    if (!exact || frames.busy()) raf = requestAnimationFrame(tick);
    else lastT = 0;
  };
  const wake = () => { if (!raf) raf = requestAnimationFrame(tick); };

  // ---- one gesture, one action ------------------------------------------------------------------------------------
  // 2 Nov (Ali): "One scroll, no matter how fast or slow you do it, should lead into one action." In the story every
  // gesture (a trackpad flick and all its momentum, a slow drag, a burst of wheel notches, one swipe on a phone, an
  // arrow key) plays exactly one action, at the action's own pace:
  //   1 the bag turns to its BaseBrix base · 2 it drops onto the grass · 3 the splash, then the pair and the £150 badge
  //   4 the two bags zip together · 5 they unzip, the yellow bag leaves and the camera moves in to the K
  //   6 club badges 1–4 through the K · 7 badges 5–8, then back out to the zipped pair, the end.
  // The page's scroll position stays the timeline (so the scroll bar, a refresh and links still work); during an action
  // the script moves it. A new gesture down at the end or up at the start is left alone, so the page scrolls on. Coming
  // back into the story from below, or after Skip, it scrubs freely with the scroll, without stops, until the visitor
  // leaves it upwards again. Timings and curves are the council's (2 Nov, evening); every action runs 25% quicker
  // backwards. Phones play at the same pace as laptops (Ali, 2 Nov 23:00: "sometimes too fast on mobile").
  const bez = (x1, y1, x2, y2) => (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    let u = t;
    for (let i = 0; i < 8; i++) {
      const x = 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u - t;
      const dx = 3 * (1 - u) * (1 - u) * x1 + 6 * (1 - u) * u * (x2 - x1) + 3 * u * u * (1 - x2);
      if (Math.abs(x) < 1e-5 || !dx) break;
      u = clamp(u - x / dx);
    }
    return 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u;
  };
  const SLOW = bez(0.37, 0, 0.63, 1), GLIDE = bez(0.33, 0, 0.2, 1), CAMERA = bez(0.65, 0, 0.35, 1);
  const EVEN = (t) => t, LAND = bez(0.4, 0.2, 0.3, 1); // LAND starts at the speed EVEN left off, then settles
  // the stops (story position p) and the actions between them, as [to, ms, curve, via] parts; `via` is a waypoint
  // [share of the curve, p]. Action 1 runs the opening (copy into the list, photo into the first frame) on into the turn.
  // Action 5 starts with an unseen cut: the held zip (0.40) and the first frame of the unzip (0.455) are the same picture.
  const STOPS = [0, 0.16, 0.22, 0.30, 0.40, 0.5645, 0.6156, STOP];
  const ACTS = [null,
    { parts: [[0.16, 2200, SLOW, [0.3, 0.09]]] },
    { parts: [[0.22, 1100, GLIDE]] },
    { parts: [[0.30, 1500, GLIDE]] },
    { parts: [[0.40, 1300, GLIDE]] },
    { cut: 0.455, parts: [[0.5645, 1900, CAMERA]] },
    { parts: [[0.6156, 1600, GLIDE]] },
    { parts: [[0.672, 1500, EVEN], [STOP, 1400, LAND]] },
  ];
  const last = STOPS.length - 1;
  const pNow = () => Math.min(STOP, rawAt(scrollY) * SPAN);
  const yAt = (p) => yFor(p / SPAN);
  let at = -1, act = null, queued = 0, played = 0;
  // between the first and last stops, and while an action plays, the pinned story also tells the browser itself not to
  // pan the page vertically (CSS touch-action), so nothing can carry the page along under a finger; at either end
  // stop it's left off so a fresh swipe can leave the story
  const lockTouch = () => flag(story, 'kb-story--locked', !free && (!!act || (at > 0 && at < STOPS.length - 1)));
  // the frame packs for the next two actions from stop i (b1 · b2 · b3 and c3 · b4); scrubbing back up into the story
  // from below, the last clip first
  const prefetch = (i) => { if (off) return; if (free) { if (zone()) frames.load([...ORDER].reverse()); } else if (i >= 0) frames.load(i === 0 ? 1 : i <= 2 ? 3 : 4); };
  const show = (i) => { at = i; story.dataset.kbStep = i < 0 ? '' : String(i); lockTouch(); prefetch(i); };
  // the stop the story rests on, if any (after a refresh, the scroll bar or a link it can be anywhere)
  const stopAt = () => { const p = pNow(); const i = STOPS.findIndex((s) => Math.abs(s - p) < 0.0015); return i; };
  // the tweens that take the story from stop i one action in direction d
  const legs = (i, d) => {
    const k = d > 0 ? i + 1 : i, a = ACTS[k];
    const from = STOPS[k - 1], quick = d > 0 ? 1 : 0.75;
    const out = [];
    let p = a.cut != null ? a.cut : from;
    if (d > 0 && a.cut != null) out.push({ cut: a.cut });
    a.parts.forEach(([to, ms, ease, via]) => { out.push({ from: p, to, ms: ms * quick, ease, via }); p = to; });
    if (d > 0) return out;
    // backwards: the same parts in reverse order, each curve mirrored, then the cut back to the held zip
    const back = out.reverse().map((l) => ({ from: l.to, to: l.from, ms: l.ms, ease: (t) => 1 - l.ease(1 - t), via: l.via && [1 - l.via[0], l.via[1]] }));
    if (a.cut != null) back.push({ cut: from });
    return back;
  };
  const posOf = (l, t) => {
    const u = l.ease(t);
    if (!l.via) return l.from + (l.to - l.from) * u;
    const [vu, vp] = l.via;
    return u < vu ? l.from + (vp - l.from) * (u / vu) : vp + (l.to - vp) * ((u - vu) / (1 - vu));
  };
  const place = (p, d) => {
    const y = yAt(p);
    window.scrollTo(0, y);
    lastScroll = y;
    dir = d;
    target = cur = p / SPAN;
    render(cur, d);
  };
  const stopAct = () => { if (act) { cancelAnimationFrame(act.raf); act = null; } queued = 0; };
  // home: the stop the action set out from, so a gesture the other way mid-action can take it back there
  const run = (list, d, end, home) => {
    stopAct();
    show(-1);
    act = { list, d, end, home, n: 0, t0: 0, raf: 0, back: list.find((l) => l.from != null)?.from ?? pNow() };
    lockTouch();
    prefetch(end);
    const frame = (now) => {
      if (!act) return;
      const l = act.list[act.n];
      if (l.cut != null) { place(l.cut, d); act.n++; act.t0 = 0; }
      else {
        if (!act.t0) act.t0 = now;
        const t = Math.min(1, (now - act.t0) / l.ms);
        place(posOf(l, t), d);
        if (t >= 1) { act.n++; act.t0 = 0; }
      }
      if (act.n < act.list.length) { act.raf = requestAnimationFrame(frame); return; }
      const q = queued;
      act = null;
      queued = 0;
      show(end);
      played++;
      story.dataset.kbActions = String(played);
      // a gesture made during the action plays next, after a short beat on this stop
      if (q) setTimeout(() => { if (!act) request(q); }, 150);
    };
    // give the action's first frames up to 300 ms to decode, so it never starts on a stand-in picture
    const t0 = performance.now();
    frames.keep(pNow(), d);
    const wait = () => { if (!act) return; if (frames.busy() && performance.now() - t0 < 300) act.raf = requestAnimationFrame(wait); else act.raf = requestAnimationFrame(frame); };
    wait();
  };
  // one action in direction d; between stops (after a refresh or the scroll bar) it goes on to the next stop
  const request = (d) => {
    if (act) {
      if (d === act.d) { queued = d; return; }
      // turned round mid-action: back to the stop it set out from (through the unseen cut, for action 5)
      const p = pNow(), home = act.home, to = act.back, ms = 300 + 900 * Math.min(1, Math.abs(p - to) / 0.08);
      const list = [{ from: p, to, ms, ease: GLIDE }];
      if (home >= 0 && Math.abs(STOPS[home] - to) > 1e-4) list.push({ cut: STOPS[home] });
      const ahead = act.end;
      run(list, d, home, ahead);
      // turning round again heads back to where the first action was going
      if (act && ahead >= 0) act.back = STOPS[ahead];
      return;
    }
    let i = at >= 0 ? at : stopAt();
    if (i < 0) {
      const p = pNow();
      const next = d > 0 ? STOPS.findIndex((s) => s > p) : last - [...STOPS].reverse().findIndex((s) => s < p);
      if (next < 0 || next > last) return;
      run([{ from: p, to: STOPS[next], ms: 400 + 800 * Math.min(1, Math.abs(STOPS[next] - p) / 0.08), ease: GLIDE }], d, next, -1);
      return;
    }
    if ((d > 0 && i >= last) || (d < 0 && i <= 0)) return;
    run(legs(i, d), d, i + d, i);
  };
  // in the story and holding the scroll: not when free, and not when the visitor is about to leave it at either end
  const zone = () => { const y = scrollY; return y >= yAt(0) - 1 && y <= yAt(STOP) + 1; };
  const leaving = (d) => !act && ((at === last && d > 0) || (at === 0 && d < 0));

  // momentum the browser won't let a page cancel (a flick that began above the story) is stopped at the story's top by
  // briefly holding the page still; the hold lifts once the wheel has been quiet for 220 ms. A hold at the story's top
  // follows the top if the header changes height on the way down (it compacts as the page scrolls).
  // Meeting 6 (3 Nov): a trackpad never goes quiet for 220 ms (momentum, then the next flick), so the hold only soaks up
  // the gesture that made it (holdGest); a new gesture lets go of it at once and plays its action
  let holdT = 0, wallY = -1, wallTop = false, wGest = 0, holdGest = -1;
  const unhold = () => { clearTimeout(holdT); holdT = 0; wallY = -1; wallTop = false; root.classList.remove('kb-story-hold'); };
  const hold = (y, top = false) => {
    wallTop = top;
    wallY = top ? yAt(0) : y;
    holdGest = wGest;
    root.classList.add('kb-story-hold');
    clearTimeout(holdT);
    holdT = setTimeout(unhold, 220);
  };

  // a wheel gesture: it starts after 200 ms of quiet, on a change of direction, or when the scroll is pushed again
  // during momentum (the gesture has faded to half its peak, then the last 3 events are twice as strong as the 3
  // before, and at least 15 px); it fires at 6 px. A steady or building run of events stays one gesture.
  let wLast = 0, wDir = 0, wSum = 0, wFired = false, wHist = [], wPeak = 0, wAbove = false;
  const mean = (a) => a.reduce((s, v) => s + v, 0) / a.length;
  const onWheel = (e) => {
    if (off) return;
    const d = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1);
    if (!d || Math.abs(e.deltaX) > Math.abs(d)) return;
    touchInput = false;
    const now = performance.now(), sd = Math.sign(d), ad = Math.abs(d);
    // wAbove: the gesture began above the story, so however hard it is, it ends at the story's top
    const above = () => !!geo && scrollY < yAt(0) - 1;
    if (now - wLast >= 200 || sd !== wDir) { wDir = sd; wSum = 0; wFired = false; wHist = []; wPeak = 0; wAbove = above(); wGest++; }
    wHist.push(ad);
    if (wHist.length > 6) wHist.shift();
    // the peak counts only events older than the last 3, so a gesture still building up is never taken for a push
    if (wHist.length >= 4) wPeak = Math.max(wPeak, wHist[wHist.length - 4]);
    const before = mean(wHist.slice(0, 3)), after = mean(wHist.slice(3));
    if (wHist.length === 6 && before <= wPeak / 2 && after >= 2 * before && after >= 15) { wSum = 0; wFired = false; wHist = [ad]; wPeak = 0; wAbove = above(); wGest++; }
    wLast = now;
    if (free || !geo) return;
    if (holdT && wGest === holdGest) { if (e.cancelable) e.preventDefault(); hold(wallY, wallTop); return; }
    if (holdT) unhold();
    const y = scrollY, y0 = yAt(0);
    // above the story: the scroll runs 1:1 down to the story's top and stops there; the rest of that gesture is spent.
    // A fast flick that began above may already have been carried past the top by the browser (its momentum can't be
    // cancelled once the page is scrolling), so it's put back and the page is held still until the flick dies away.
    if (sd > 0 && (wAbove || y < y0 - 1)) {
      if (y + d > y0 - 1) {
        if (e.cancelable) e.preventDefault();
        if (!act) { hold(y0, true); if (Math.abs(scrollY - wallY) > 1) window.scrollTo(0, wallY); }
        if (!wFired) { show(0); wFired = true; }
      }
      return;
    }
    if (y < y0 - 1) return;
    if (!zone()) return;
    // leaving at either end takes a new gesture: what is left of the one that played the last action stays spent
    if (leaving(sd) && !wFired) return;
    if (e.cancelable) e.preventDefault();
    else if (!act) hold(y);
    wSum += ad;
    if (!wFired && wSum >= 6) { wFired = true; request(sd); }
  };

  // a touch is one gesture: in the story the page doesn't move under the finger; 24 px of travel plays one action
  let tY = 0, tX = 0, tFired = false, tMode = 0, touching = false, liftAt = 0, tTravel = 0, tWall = false;
  const onTouchStart = (e) => {
    touchInput = true; touching = true; tFired = false; tMode = 0; tTravel = 0; tWall = false;
    tY = e.touches[0].clientY; tX = e.touches[0].clientX;
    // a new touch lets go of a hold left by the last swipe (the touch itself has stopped any momentum)
    if (holdT) unhold();
  };
  const onTouchMove = (e) => {
    const t = e.touches[0], dy = tY - t.clientY, dx = tX - t.clientX;
    tTravel = Math.max(tTravel, Math.abs(dy));
    // this swipe came down from above and was stopped at the story's top: the rest of it is spent
    if (tWall) { if (wallY >= 0) hold(wallY, wallTop); return; }
    if (off || free || tMode === 2 || !geo) return;
    if (!tMode) {
      // decided on the first move that has a direction (a touch can report a move before the finger has travelled)
      if (!dx && !dy) return;
      // sideways (the colour dots in the buy bar), outside the story, or leaving it at either end: the page's own scroll
      tMode = Math.abs(dx) > Math.abs(dy) || !zone() || (dy && leaving(Math.sign(dy))) ? 2 : 1;
      if (tMode === 2) return;
    }
    if (e.cancelable) e.preventDefault();
    if (!tFired && Math.abs(dy) >= 24) { tFired = true; request(Math.sign(dy)); }
  };
  // a swipe's momentum carries on after the finger lifts (a tap on a link doesn't count: it has no travel)
  const onTouchEnd = () => { touching = false; if (tTravel >= 10) liftAt = performance.now(); };

  // keys: ↓, Page Down and Space forward; ↑, Page Up and Shift+Space back (never in a field, a button or the buy bar)
  const onKey = (e) => {
    if (off || free || e.repeat || e.altKey || e.ctrlKey || e.metaKey || !geo) return;
    if (e.target.closest && e.target.closest('input, textarea, select, button, a, [contenteditable], .kb-story__buy')) return;
    const fwd = e.key === 'ArrowDown' || e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey);
    const bwd = e.key === 'ArrowUp' || e.key === 'PageUp' || (e.key === ' ' && e.shiftKey);
    if (!fwd && !bwd) return;
    const d = fwd ? 1 : -1;
    if (!zone() || leaving(d)) return;
    e.preventDefault();
    request(d);
  };

  let lastScroll = scrollY;
  const onScroll = () => {
    if (off) return;
    if (!geo) measure();
    let y = scrollY;
    const y0 = yAt(0), y1 = yAt(STOP);
    // a swipe or flick from above (the finger, or the momentum of either) runs down to the story's top and stops there;
    // the next gesture plays action 1. The browser can move the page before the wheel handler hears about it, so the
    // crossing is caught here too.
    const fromAbove = touchInput ? touching || performance.now() - liftAt < 2500 : wAbove && performance.now() - wLast < 300;
    if (!free && !act && lastScroll < y0 - 1 && y > y0 - 1 && fromAbove) {
      hold(y0, true);
      show(0);
      if (touchInput && touching) tWall = true;
      if (!touchInput) wFired = true;
    }
    if (wallY >= 0 && !act) {
      if (wallTop) wallY = yAt(0);
      if (Math.abs(y - wallY) > 1) { window.scrollTo(0, wallY); y = wallY; hold(wallY, wallTop); }
    }
    if (y !== lastScroll) dir = y > lastScroll ? 1 : -1;
    // free scrubbing below the story's end (and after it); stepped again once the visitor is back above its top
    if (!ship) {
      if (y > y1 + 1 && !free) { free = true; lockTouch(); }
      else if (y < y0 - 1) { free = false; if (!act) show(-1); }
    }
    lastScroll = y;
    if (free) prefetch(-1);
    if (!act) {
      target = rawAt(y);
      if (!free && at >= 0 && Math.abs(y - yAt(STOPS[at])) > 2) show(stopAt());
    }
    // while the story is pinned the header drops its frosted blur (it would be recomputed every frame)
    flag(root, 'kb-story-pinned', target > 0 && target < 1);
    if (target !== cur || dirty) wake();
  };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('wheel', onWheel, { passive: false });
  addEventListener('keydown', onKey);
  addEventListener('touchstart', onTouchStart, { passive: true });
  addEventListener('touchmove', onTouchMove, { passive: false });
  addEventListener('touchend', onTouchEnd, { passive: true });
  addEventListener('touchcancel', onTouchEnd, { passive: true });

  // "Skip the story": straight past the pin to the next section, for anyone who came to shop
  const skip = document.createElement('a');
  skip.className = 'kb-story__skip';
  skip.href = '#';
  skip.innerHTML = 'Skip the story <span aria-hidden="true">↓</span>';
  skip.addEventListener('click', (e) => {
    e.preventDefault();
    stopAct();
    free = true;
    show(-1);
    const next = story.closest('.shopify-section')?.nextElementSibling;
    const y = Math.round(geo.top + story.offsetHeight - geo.stuck + 1);
    window.scrollTo({ top: y, behavior: 'smooth' });
    if (next) next.setAttribute('tabindex', '-1');
    setTimeout(() => next && next.focus({ preventScroll: true }), 600);
  });
  pin.insertBefore(skip, pin.firstChild);

  // too slow to scrub (an old phone, or the visitor saves data): the plain picture with the four points instead.
  // Decided once, before the visitor has started the story, and never switched while it is running.
  const goStatic = () => {
    off = true;
    stopAct();
    flag(root, 'kb-story-pinned', false);
    skip.remove();
    story.classList.add('kb-story--static');
  };
  if (navigator.connection && navigator.connection.saveData) { goStatic(); return; }
  const probe = () => {
    if (window.Shopify && window.Shopify.designMode) return;
    const gaps = [];
    let last = 0;
    const f = (now) => {
      if (last) gaps.push(now - last);
      last = now;
      if (gaps.length < 30) { requestAnimationFrame(f); return; }
      gaps.sort((a, b) => a - b);
      if (gaps[15] > 40 && rawAt(scrollY) === 0 && cur === 0) goStatic();
    };
    requestAnimationFrame(f);
  };
  if (document.readyState === 'complete') setTimeout(probe, 400);
  else addEventListener('load', () => setTimeout(probe, 400), { once: true });

  const remeasure = () => {
    if (off) return;
    geo = null;
    measure();
    // held at the story's top while the header changed height: stay on the top
    if (holdT && wallTop && !act) { wallY = yAt(0); window.scrollTo(0, wallY); }
    target = rawAt(scrollY);
    dirty = true;
    wake();
  };
  story.classList.add('is-ready');
  put(bar, 'width', '100%');
  put(bar, 'transformOrigin', '0 50%');
  addEventListener('resize', remeasure);
  phone.addEventListener('change', remeasure);
  if (window.ResizeObserver) new ResizeObserver(remeasure).observe(header || story);
  // the header compacts after scrolling: when its height changes, measure again so the pin stays right under it
  new MutationObserver(() => { const h = headerH(); if (geo && h >= 0 && Math.abs(h - geo.stuck) > 1) remeasure(); })
    .observe(root, { attributes: true, attributeFilter: ['style'] });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
  measure();
  // opened (or restored after a refresh) part-way through the story: rest on the stop before
  const settleIn = () => {
    if (free || act || !zone()) return;
    const i = stopAt();
    if (i >= 0) { show(i); return; }
    const p = pNow(), before = last - [...STOPS].reverse().findIndex((s) => s < p);
    if (before >= 0 && before <= last) { window.scrollTo(0, yAt(STOPS[before])); show(before); }
  };
  settleIn();
  addEventListener('load', () => setTimeout(settleIn, 0), { once: true });
  cur = target = rawAt(scrollY);
  render(cur, 1);
};
document.querySelectorAll('[data-kb-story]:not(.kb-story--static)').forEach(init);
document.addEventListener('shopify:section:load', (e) => {
  const story = e.target.querySelector('[data-kb-story]:not(.kb-story--static)');
  if (story) init(story);
});
})();
